import {
  ChangeDetectionStrategy,
  Component,
  effect,
  ElementRef,
  HostListener,
  Injector,
  afterNextRender,
  inject,
  signal,
  viewChild,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Company } from '@features/company/models/company';
import { CompanyContextService } from '@core/tenancy/company-context.service';
import { CompanyService } from '@features/company/data/company.service';

@Component({
  selector: 'app-company-dropdown',
  templateUrl: './company-dropdown.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CompanyDropdownComponent {
  protected readonly companyContext = inject(CompanyContextService);
  private readonly companyService = inject(CompanyService);
  private readonly elRef = inject(ElementRef);
  private readonly injector = inject(Injector);
  private static nextId = 0;
  protected readonly listId = `company-list-${CompanyDropdownComponent.nextId++}`;
  private readonly trigger = viewChild.required<ElementRef<HTMLButtonElement>>('trigger');
  private readonly list = viewChild<ElementRef<HTMLUListElement>>('list');

  protected readonly isOpen = signal(false);
  protected readonly companies = toSignal(this.companyService.getCompaniesForCurrentUser(), {
    initialValue: [] as Company[],
  });
  protected readonly selectedName = this.companyContext.selectedName;

  constructor() {
    effect(() => {
      const list = this.companies();
      if (list.length === 0) return;
      const current = this.companyContext.selected();
      if (!current) {
        this.companyContext.select(list[0]);
        return;
      }
      // Re-sync from the freshly fetched list (no extra request) so currency changes and
      // selections stored before currency support are picked up.
      const fresh = list.find((c) => c.id === current.id);
      if (
        fresh &&
        (fresh.currencyCode !== current.currencyCode ||
          fresh.currencySymbol !== current.currencySymbol ||
          fresh.name !== current.name)
      ) {
        this.companyContext.select(fresh);
      }
    });
  }

  protected toggleOpen(): void {
    this.isOpen.update((v) => !v);
    if (this.isOpen()) this.focusOption();
  }

  protected openFromKeyboard(event: Event): void {
    event.preventDefault();
    this.isOpen.set(true);
    this.focusOption();
  }

  protected select(company: Company): void {
    this.companyContext.select(company);
    this.close();
  }

  /** Listbox keys: Up/Down/Home/End move between companies, Enter/Space choose, Escape closes, Tab leaves. */
  protected onListKeydown(event: KeyboardEvent): void {
    const options = this.options();
    const index = options.indexOf(document.activeElement as HTMLElement);
    const move = (to: number) => {
      event.preventDefault();
      options[Math.max(0, Math.min(options.length - 1, to))]?.focus();
    };
    switch (event.key) {
      case 'ArrowDown':
        return move(index + 1);
      case 'ArrowUp':
        return move(index - 1);
      case 'Home':
        return move(0);
      case 'End':
        return move(options.length - 1);
      case 'Enter':
      case ' ': {
        event.preventDefault();
        const company = this.companies()[index];
        if (company) this.select(company);
        return;
      }
      case 'Escape':
        event.preventDefault();
        event.stopPropagation();
        return this.close();
      case 'Tab':
        this.isOpen.set(false);
        return;
    }
  }

  /** Closes the list and returns focus to the button. */
  private close(): void {
    this.isOpen.set(false);
    this.trigger().nativeElement.focus();
  }

  private options(): HTMLElement[] {
    return Array.from(this.list()?.nativeElement.querySelectorAll<HTMLElement>('[role="option"]') ?? []);
  }

  /** Focuses the selected company (else the first) once the list has rendered. */
  private focusOption(): void {
    afterNextRender(
      () => {
        const options = this.options();
        (options.find((o) => o.getAttribute('aria-selected') === 'true') ?? options[0])?.focus();
      },
      { injector: this.injector },
    );
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (!this.elRef.nativeElement.contains(event.target)) {
      this.isOpen.set(false);
    }
  }
}
